import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Text
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { ImportEntityStatuses, ImportFileTypes } from '../../../constants/importStatuses';
import { createErrorToast, createSuccessToast } from '../../../redux/modules/app';
import { hideModal } from '../../../redux/modules/modal';
import { useUpdateImportRow } from '../../../services/import-service';

import { sortedIssues } from './util';

// [manifest column, label, camel-cased key on the row, select options]
const SECTIONS = [
  {
    title: 'Insured',
    fields: [
      ['insured_name', 'Insured name', 'insuredName'],
      ['insured_email', 'Insured email', 'insuredEmail'],
      ['insured_cc_email', 'Insured cc emails (comma-separated)', 'insuredCcEmail'],
      ['insured_primary_phone', 'Primary phone', 'insuredPrimaryPhone'],
      ['insured_secondary_phone', 'Secondary phone', 'insuredSecondaryPhone'],
      ['insured_address', 'Address', 'insuredAddress'],
      ['insured_city', 'City', 'insuredCity'],
      ['insured_state', 'State (two letters)', 'insuredState'],
      ['insured_zip', 'Zip', 'insuredZip'],
      ['insured_country', 'Country', 'insuredCountry']
    ]
  },
  {
    title: 'Project',
    fields: [
      ['project', 'Project', 'project'],
      ['department', 'Department', 'department'],
      ['project_number', 'Project number', 'projectNumber'],
      ['project_description', 'Project description', 'projectDescription'],
      ['project_start_date', 'Start date (yyyy-mm-dd)', 'projectStartDate'],
      ['project_end_date', 'End date (yyyy-mm-dd)', 'projectEndDate'],
      ['project_owner_id', 'Owner user id', 'projectOwnerId'],
      ['project_default_requirement_template_id', 'Default template id', 'projectDefaultRequirementTemplateId']
    ]
  },
  {
    title: 'Record',
    fields: [
      ['record_key', 'Record key', 'recordKey'],
      ['contact_email', 'Record contact emails (comma-separated)', 'contactEmail'],
      ['contract_number', 'Contract number', 'contractNumber'],
      ['template', 'Template (published company template name)', 'template'],
      ['requirement_template_id', 'Template id (wins over the name)', 'requirementTemplateId'],
      ['record_status', 'Record status', 'recordStatus', ImportEntityStatuses]
    ]
  },
  {
    title: 'File',
    fields: [
      ['file_type', 'File type', 'fileType', ImportFileTypes],
      ['file_status', 'File status', 'fileStatus', ImportEntityStatuses]
    ]
  }
];

const FIELDS = SECTIONS.flatMap(section => section.fields);

const current = (row, camel) => row.overrides?.[camel] ?? row.data?.[camel] ?? '';

/**
 * Per-row overrides: a blank field clears its override and falls back to the
 * manifest value; the API resets the row to pending and the import to created.
 */
const EditImportRowModal = ({ importId, row }) => {
  const dispatch = useDispatch();
  const { mutateAsync: updateRow, isLoading } = useUpdateImportRow(importId);

  const defaults = {};

  FIELDS.forEach(([name, , camel]) => { defaults[name] = current(row, camel); });
  defaults.insured_id = row.overrides?.insuredId ?? row.data?.insuredId ?? '';
  defaults.new_insured = Boolean(row.overrides?.newInsured ?? row.data?.newInsured ?? false);

  const useFormMethods = useForm({ defaultValues: defaults });
  const { handleSubmit, register } = useFormMethods;

  const onSubmit = async values => {
    const overrides = { rowId: row.id };

    FIELDS.forEach(([name]) => {
      const value = typeof values[name] === 'string' ? values[name].trim() : values[name];

      if (value !== defaults[name]) {
        overrides[name] = value === '' ? null : value;
      }
    });

    const insuredId = values.insured_id === '' ? null : Number(values.insured_id);

    if ((defaults.insured_id === '' ? null : Number(defaults.insured_id)) !== insuredId) {
      overrides.insured_id = insuredId;
    }

    if (Boolean(values.new_insured) !== defaults.new_insured) {
      overrides.new_insured = values.new_insured ? true : null;
    }

    if (Object.keys(overrides).length === 1) {
      dispatch(hideModal());
      return;
    }

    try {
      await updateRow(overrides);
      dispatch(createSuccessToast({ title: 'Row updated', description: 'Validate again to re-check the import.' }));
      dispatch(hideModal());
    } catch (error) {
      dispatch(createErrorToast({ title: 'The row could not be updated', description: error?.message, error }));
    }
  };

  const issues = sortedIssues(row);
  const candidates = row.resolved?.insuredCandidates ?? [];

  return (
    <Box>
      <form data-testid="edit-row-form" onSubmit={handleSubmit(onSubmit)}>
        {issues.length > 0 && (
          <Box bg="greyScale.100" borderRadius="md" mb={4} p={3}>
            {issues.map(issue => (
              <Text color={issue.level === 'error' ? 'red.600' : 'orange.600'} key={`${issue.code}-${issue.message}`} textStyle="bodySm">{issue.message}</Text>
            ))}
          </Box>
        )}

        {SECTIONS.map(section => (
          <Box key={section.title} mb={4}>
            <Text mb={2} textStyle="bodyLg500">{section.title}</Text>
            {section.title === 'Insured' && (
              <SimpleGrid columns={2} mb={4} spacing={4}>
                <FormControl>
                  <FormLabel>Pin to insured id</FormLabel>
                  <Input data-testid="field-insured_id" type="number" {...register('insured_id')} />
                  {candidates.length > 0 && (
                    <FormHelperText>
                      {`Candidates: ${candidates.map(candidate => `#${candidate.insuredId} ${candidate.name} (${candidate.similarity}%)`).join(', ')}`}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl pt={8}>
                  <Checkbox data-testid="field-new_insured" {...register('new_insured')}>Create as a new insured</Checkbox>
                </FormControl>
              </SimpleGrid>
            )}
            <SimpleGrid columns={2} spacing={4}>
              {section.fields.map(([name, label, , options]) => (
                <FormControl key={name}>
                  <FormLabel>{label}</FormLabel>
                  {options ? (
                    <Select data-testid={`field-${name}`} {...register(name)}>
                      <option value="">(from manifest / inferred)</option>
                      {options.map(option => <option key={option} value={option}>{option}</option>)}
                    </Select>
                  ) : (
                    <Input data-testid={`field-${name}`} {...register(name)} />
                  )}
                </FormControl>
              ))}
            </SimpleGrid>
          </Box>
        ))}

        <HStack justifyContent="center" spacing={4}>
          <Button variant="secondary" onClick={() => dispatch(hideModal())}>Cancel</Button>
          <Button isLoading={isLoading} type="submit">Save overrides</Button>
        </HStack>
      </form>
    </Box>
  );
};

EditImportRowModal.propTypes = {
  importId: PropTypes.number.isRequired,
  row: PropTypes.object.isRequired
};

export default EditImportRowModal;
